/**
 * График агрегата: минутные свечи, линия P₀, индикаторы, активность, линия собственного прогноза.
 * До этапа 2 шкала цен скрыта (данные приходят в нормированных единицах), с этапа 2 —
 * шкала в пунктах с расстоянием от P₀.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type SeriesMarker,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { ChartPayload, Direction } from '@mt/protocol';
import { bollinger, ema, rsi, sma, vwap, type Point } from '../lib/indicators';
import { updateSettings, useSettings, type IndicatorSettings } from '../lib/settings';

interface Props {
  chart: ChartPayload | null;
  startAt: number;
  minuteMs: number;
  forecast: { direction: Direction | null; points: number | null };
  settled: boolean;
}

const ts = (t: number) => Math.floor(t / 1000) as UTCTimestamp;

function localTime(t: number, seconds: boolean): string {
  return new Date(t * 1000).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', ...(seconds ? { second: '2-digit' } : {}) });
}

function css(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function Chart({ chart, startAt, minuteMs, forecast, settled }: Props) {
  const el = useRef<HTMLDivElement>(null);
  const api = useRef<{
    chart: IChartApi;
    candles: ISeriesApi<'Candlestick'>;
    sma: ISeriesApi<'Line'>;
    ema: ISeriesApi<'Line'>;
    vwap: ISeriesApi<'Line'>;
    bbU: ISeriesApi<'Line'>;
    bbL: ISeriesApi<'Line'>;
    bbM: ISeriesApi<'Line'>;
    rsi: ISeriesApi<'Line'>;
    act: ISeriesApi<'Histogram'>;
    p0Line: IPriceLine | null;
    fLine: IPriceLine | null;
  } | null>(null);
  const settings = useSettings();
  // номер экземпляра графика: при пересоздании (смена темы, длительности минуты) все эффекты данных
  // должны выполниться заново, иначе новый график останется пустым
  const [epoch, setEpoch] = useState(0);
  /** для какого розыгрыша и экземпляра графика уже выполнено «по ширине» */
  const fitted = useRef<string | null>(null);
  const ind = settings.indicators;
  const points = chart?.units === 'points';

  // создание графика
  useEffect(() => {
    if (!el.current) return;
    const c = createChart(el.current, {
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: css('--muted'), fontFamily: css('--font-mono'), fontSize: 11 },
      grid: { vertLines: { color: css('--grid') }, horzLines: { color: css('--grid') } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.06, bottom: 0.2 } },
      timeScale: {
        borderVisible: false,
        timeVisible: true,
        secondsVisible: minuteMs < 60_000,
        rightOffset: 2,
        // свечи не «уезжают» вправо: левый край прижат к первому бару
        fixLeftEdge: true,
        tickMarkFormatter: (t: Time) => localTime(t as number, minuteMs < 60_000),
      },
      handleScale: { axisPressedMouseMove: { time: true, price: false } },
      // время на оси — локальное (библиотека по умолчанию показывает UTC)
      localization: { timeFormatter: (t: Time) => localTime(t as number, true) },
    });
    const line = (color: string, width: 1 | 2 = 1, style: LineStyle = LineStyle.Solid) =>
      c.addLineSeries({ color, lineWidth: width, lineStyle: style, priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false });
    const candles = c.addCandlestickSeries({
      upColor: css('--up'),
      downColor: css('--down'),
      wickUpColor: css('--up'),
      wickDownColor: css('--down'),
      borderVisible: false,
      priceLineVisible: false,
    });
    const act = c.addHistogramSeries({ priceScaleId: 'act', priceFormat: { type: 'volume' }, lastValueVisible: false, priceLineVisible: false });
    const rsiS = c.addLineSeries({ priceScaleId: 'rsi', color: css('--ind-rsi'), lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
    api.current = {
      chart: c,
      candles,
      sma: line(css('--ind-sma'), 2),
      ema: line(css('--ind-ema'), 1),
      vwap: line(css('--ind-vwap'), 1, LineStyle.Dashed),
      bbU: line(css('--ind-bb')),
      bbL: line(css('--ind-bb')),
      bbM: line(css('--ind-bb'), 1, LineStyle.Dotted),
      rsi: rsiS,
      act,
      p0Line: null,
      fLine: null,
    };
    setEpoch((e) => e + 1);
    // при изменении ширины контейнера (поворот телефона, сворачивание панелей) — снова на всю ширину
    let lastWidth = el.current.clientWidth;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry?.contentRect.width ?? 0);
      if (w && Math.abs(w - lastWidth) > 4) {
        lastWidth = w;
        requestAnimationFrame(() => c.timeScale().fitContent());
      }
    });
    ro.observe(el.current);
    return () => {
      ro.disconnect();
      c.remove();
      api.current = null;
    };
  }, [minuteMs, settings.theme]);

  const bars = chart?.bars ?? [];

  // данные свечей и пустые слоты будущих минут (до целевой свечи)
  useEffect(() => {
    const a = api.current;
    if (!a) return;
    const data: Parameters<typeof a.candles.setData>[0] = bars.map((b) => ({ time: ts(b.t), open: b.o, high: b.h, low: b.l, close: b.c }));
    const last = bars.length ? bars[bars.length - 1]!.t : startAt - minuteMs;
    for (let t = last + minuteMs; t <= startAt + 3 * minuteMs; t += minuteMs) data.push({ time: ts(t) });
    a.candles.setData(data);
    a.act.setData(bars.map((b) => ({ time: ts(b.t), value: b.v, color: b.c >= b.o ? css('--up-soft') : css('--down-soft') })));
    const markers: SeriesMarker<Time>[] = [];
    const p0Bar = bars.find((b) => b.t === startAt - minuteMs);
    if (p0Bar) markers.push({ time: ts(p0Bar.t), position: 'aboveBar', color: css('--accent'), shape: 'arrowDown', text: 'P₀' });
    const target = bars.find((b) => b.t === startAt + 3 * minuteMs);
    if (target && settled) markers.push({ time: ts(target.t), position: 'aboveBar', color: css('--accent'), shape: 'arrowDown', text: 'P₁' });
    a.candles.setMarkers(markers);
    // первый показ данных розыгрыша (и после пересоздания графика) — вся история на ширину области
    const key = `${chart?.gameId ?? ''}:${epoch}`;
    if (data.length && fitted.current !== key) {
      fitted.current = key;
      a.chart.timeScale().fitContent();
    }
  }, [bars, startAt, minuteMs, settled, epoch]);

  // индикаторы
  const series = useMemo(() => {
    const toLine = (p: Point[]) => p.map((x) => ({ time: ts(x.t), value: x.v }));
    const bb = bollinger(bars, ind.bb.period, ind.bb.mult);
    return {
      sma: toLine(sma(bars, ind.sma.period)),
      ema: toLine(ema(bars, ind.ema.period)),
      vwap: toLine(vwap(bars)),
      bbU: toLine(bb.upper),
      bbL: toLine(bb.lower),
      bbM: toLine(bb.mid),
      rsi: toLine(rsi(bars, ind.rsi.period)),
    };
  }, [bars, ind.sma.period, ind.ema.period, ind.bb.period, ind.bb.mult, ind.rsi.period]);

  useEffect(() => {
    const a = api.current;
    if (!a) return;
    a.sma.setData(ind.sma.on ? series.sma : []);
    a.ema.setData(ind.ema.on ? series.ema : []);
    a.vwap.setData(ind.vwap.on ? series.vwap : []);
    a.bbU.setData(ind.bb.on ? series.bbU : []);
    a.bbL.setData(ind.bb.on ? series.bbL : []);
    a.bbM.setData(ind.bb.on ? series.bbM : []);
    a.rsi.setData(ind.rsi.on ? series.rsi : []);
    a.act.applyOptions({ visible: ind.activity.on });
    // нижние панели: активность и RSI
    const bottom = (ind.activity.on ? 0.14 : 0) + (ind.rsi.on ? 0.16 : 0);
    a.chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.06, bottom: Math.max(0.04, bottom + 0.02) } });
    a.chart.priceScale('act').applyOptions({ scaleMargins: { top: 0.88, bottom: 0 }, visible: false });
    a.chart.priceScale('rsi').applyOptions({ scaleMargins: { top: ind.activity.on ? 0.7 : 0.84, bottom: ind.activity.on ? 0.14 : 0 }, visible: false });
  }, [series, ind, epoch]);

  // шкала: скрыта до этапа 2; затем цены и расстояние от P₀ в пунктах
  useEffect(() => {
    const a = api.current;
    if (!a || !chart) return;
    const p0 = chart.p0Points;
    a.chart.applyOptions({
      rightPriceScale: { visible: points },
      crosshair: { horzLine: { labelVisible: points } },
      localization: {
        timeFormatter: (t: Time) => localTime(t as number, true),
        priceFormatter: (p: number) => {
          if (!points) return '';
          const d = p - p0;
          return `${p.toFixed(2)}  ${d >= 0 ? '+' : '−'}${Math.abs(d).toFixed(2)}`;
        },
      },
    });
    a.candles.applyOptions({ lastValueVisible: points });
    if (a.p0Line) a.candles.removePriceLine(a.p0Line);
    a.p0Line = a.candles.createPriceLine({
      price: chart.p0,
      color: css('--accent'),
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: points,
      title: 'P₀',
    });
  }, [chart?.units, chart?.p0, chart?.p0Points, points, chart, epoch]);

  // линия собственного прогноза (P₀ ± пункты) — только когда видна шкала
  useEffect(() => {
    const a = api.current;
    if (!a || !chart) return;
    if (a.fLine) {
      a.candles.removePriceLine(a.fLine);
      a.fLine = null;
    }
    if (points && forecast.direction && forecast.points) {
      const price = chart.p0Points + (forecast.direction === 'up' ? 1 : -1) * forecast.points;
      a.fLine = a.candles.createPriceLine({
        price,
        color: forecast.direction === 'up' ? css('--up') : css('--down'),
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        axisLabelVisible: true,
        title: 'мой прогноз',
      });
    }
  }, [points, forecast.direction, forecast.points, chart, epoch]);

  return (
    <div className="chart-wrap">
      <IndicatorBar ind={ind} />
      <div className="chart" ref={el} />
      {!points && chart && <div className="chart-hint">Шкала цен скрыта до круга 2 · единицы нормированы</div>}
      {!chart && <div className="chart-empty">Ожидание данных агрегата…</div>}
    </div>
  );
}

function IndicatorBar({ ind }: { ind: IndicatorSettings }) {
  const toggle = (k: keyof IndicatorSettings) =>
    updateSettings((s) => ({ ...s, indicators: { ...s.indicators, [k]: { ...s.indicators[k], on: !s.indicators[k].on } } }));
  const setNum = (k: 'sma' | 'ema' | 'rsi' | 'bb', field: 'period' | 'mult', v: number) =>
    updateSettings((s) => ({ ...s, indicators: { ...s.indicators, [k]: { ...s.indicators[k], [field]: v } } }));
  const chip = (k: keyof IndicatorSettings, label: string, color: string, extra?: React.ReactNode) => (
    <span className={`chip ${ind[k].on ? 'on' : ''}`}>
      <button type="button" onClick={() => toggle(k)} title={ind[k].on ? 'Скрыть' : 'Показать'}>
        <i style={{ background: `var(${color})` }} />
        {label}
      </button>
      {ind[k].on && extra}
    </span>
  );
  const period = (k: 'sma' | 'ema' | 'rsi' | 'bb', v: number, min = 2, max = 200) => (
    <input
      className="chip-num"
      type="number"
      min={min}
      max={max}
      value={v}
      aria-label="период"
      onChange={(e) => {
        const n = Math.round(Number(e.target.value));
        if (n >= min && n <= max) setNum(k, 'period', n);
      }}
    />
  );
  return (
    <div className="ind-bar" role="toolbar" aria-label="Индикаторы">
      {chip('sma', 'SMA', '--ind-sma', period('sma', ind.sma.period))}
      {chip('ema', 'EMA', '--ind-ema', period('ema', ind.ema.period))}
      {chip('vwap', 'VWAP*', '--ind-vwap')}
      {chip('bb', 'BB', '--ind-bb', period('bb', ind.bb.period))}
      {chip('rsi', 'RSI', '--ind-rsi', period('rsi', ind.rsi.period))}
      {chip('activity', 'Активность', '--muted')}
    </div>
  );
}
